import {
  type BoxProps,
  FormControl,
  FormErrorMessage,
  FormLabel,
  type SystemStyleObject,
} from "@chakra-ui/react";
import {
  type ChakraStylesConfig,
  chakraComponents,
  type GroupBase,
  type MultiValue,
  type OnChangeValue,
  CreatableSelect as ReactCreatableSelect,
  Select as ReactSelect,
  type Props as ReactSelectProps,
  type SelectInstance,
  type SetValueAction,
  type SingleValue,
  type StylesConfig,
} from "chakra-react-select";
import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { CustomControl, CustomMenuList } from "./components";
import { VirtualizedMenuList } from "./components/virtualized-menu-list";
import { createFilterOption } from "./filterOption";
import { extractOptions } from "./helpers";
import type { IOption, SelectOptions } from "./types";

const OPTION_PREFIX = "select-value-";
const DEFAULT_VIRTUALIZE_MENU_MIN_OPTIONS = 100;

type SelectInternalProps = Omit<ReactSelectProps, "onChange"> & {
  options: SelectOptions;
  topExtraContent?: ReactNode;
  bottomExtraContent?: ReactNode;
  prefix?: ReactNode;
  isCreatable?: boolean;
  asPortal?: boolean;
  onChange?: (val: any) => void;
};

export type SelectProps = SelectInternalProps & {
  error?: string;
  label?: string;
  menuMinWidth?: BoxProps["minW"];
  ignoreFilterOptions?: boolean;
  virtualizeMenu?: boolean;
  virtualizeMenuMinOptions?: number;
  virtualizedOptionHeight?: number;
  virtualizedOverscan?: number;
};

export interface SelectRef {
  onMenuClose: () => void | undefined;
  setValue: (val: unknown, action: SetValueAction, option?: unknown) => void;
  focus: () => void;
}

export const SelectInternal = forwardRef<SelectRef, SelectProps>(
  function SelectInternal(
    {
      value,
      onChange,
      placeholder,
      topExtraContent,
      bottomExtraContent,
      components,
      prefix,
      isDisabled,
      isLoading,
      isClearable = false,
      isCreatable,
      isSearchable = true,
      isMulti = false,
      asPortal,
      chakraStyles: customChakraStyles,
      menuMinWidth = "auto",
      ignoreFilterOptions,
      styles: customStyles,
      virtualizeMenu = true,
      virtualizeMenuMinOptions = DEFAULT_VIRTUALIZE_MENU_MIN_OPTIONS,
      virtualizedOptionHeight = 40,
      virtualizedOverscan = 8,
      ...props
    },
    ref,
  ) {
    const Component = isCreatable ? ReactCreatableSelect : ReactSelect;
    const selectRef = useRef<SelectInstance>(null);
    useImperativeHandle(ref, () => ({
      onMenuClose() {
        selectRef.current?.onMenuClose();
      },
      blur() {
        selectRef.current?.blur();
      },
      focus() {
        selectRef.current?.focus();
      },
      setValue(value, action, option) {
        selectRef.current?.setValue(value, action, option);
      },
    }));

    const [selected, setSelected] =
      useState<OnChangeValue<IOption, typeof isMulti>>();

    function parseValues(data: OnChangeValue<IOption, typeof isMulti>): any[] {
      const selectedOptions: IOption[] = Array.isArray(data)
        ? data
        : data
          ? [data]
          : [];
      return selectedOptions.map((i) => i.value);
    }

    const optionsMap = useMemo(() => {
      const memOptions = Array.isArray(selected) ? selected : [selected];
      const availableOptions = selected
        ? extractOptions([...memOptions, ...props.options])
        : extractOptions(props.options);
      return new Map(availableOptions.map((i) => [i.value, i]));
    }, [props.options]);
    const selectFilterOption = useMemo(
      () => createFilterOption(props.options),
      [props.options],
    );
    const shouldVirtualizeMenu = useMemo(() => {
      if (virtualizeMenu === false) return false;
      if (!Array.isArray(props.options) || !props.options.length) return false;
      if ("options" in props.options[0]) return false;
      return props.options.length >= virtualizeMenuMinOptions;
    }, [props.options, virtualizeMenu, virtualizeMenuMinOptions]);

    useEffect(() => {
      const _values: string[] = Array.isArray(value)
        ? value
        : value
          ? [value]
          : [];
      const _selected = _values.flatMap((v) => {
        const opt = optionsMap.get(v);
        return opt ? [opt] : [];
      });
      setSelected(_selected);
    }, [value, optionsMap]);

    const setSelectedOnSelectChange = useCallback(
      (data: SingleValue<IOption> | MultiValue<IOption>) => {
        setSelected(data);
      },
      [setSelected],
    );

    const handleMenuOpen = () => {
      props.onMenuOpen?.();
      // Time outing because it takes a while for menu to mount
      setTimeout(() => {
        if (!selectRef.current?.menuListRef) return;
        if (!value) return;

        const selectedOption = document.getElementById(
          `${OPTION_PREFIX}${value}`,
        );
        if (!selectedOption) return;

        selectRef.current?.menuListRef?.scrollTo({
          top: selectedOption.offsetTop - 10,
        });
      }, 100);
    };

    const memoizedComponents = useMemo(
      () => ({
        IndicatorSeparator: () => null,
        ...(shouldVirtualizeMenu && {
          MenuList: (props: any) => (
            <VirtualizedMenuList
              menuProps={props}
              topExtraContent={topExtraContent}
              bottomExtraContent={bottomExtraContent}
              optionHeight={virtualizedOptionHeight}
              overscan={virtualizedOverscan}
            />
          ),
        }),
        ...(!shouldVirtualizeMenu &&
          (topExtraContent || bottomExtraContent) && {
            MenuList: (props: any) =>
              CustomMenuList(props, topExtraContent, bottomExtraContent),
          }),
        Control: (props: any) => CustomControl(props, { prefix }),
        ...components,
        Option: (props: any) => (
          <chakraComponents.Option {...props}>
            <div id={`${OPTION_PREFIX}${props?.data?.value ?? ""}`}>
              {props.children}
            </div>
          </chakraComponents.Option>
        ),
      }),
      [
        bottomExtraContent,
        components,
        prefix,
        shouldVirtualizeMenu,
        topExtraContent,
        virtualizedOptionHeight,
        virtualizedOverscan,
      ],
    );

    const mergedChakraStyles = useMemo(
      () => ({
        menu: (provided: SystemStyleObject) => ({
          ...provided,
          zIndex: 9999,
          shadow: "lg",
        }),
        control: (provided: SystemStyleObject) => ({
          ...provided,
          minH: 9,
          h: "auto",
          borderRadius: "md",
        }),
        menuList: (provided: SystemStyleObject) => ({
          ...provided,
          minW: menuMinWidth,
          borderRadius: "0",
        }),
        multiValueLabel: (provided: SystemStyleObject) => ({
          ...provided,
          fontSize: "xs",
        }),
        multiValueRemove: (provided: SystemStyleObject) => ({
          ...provided,
          ":hover": {
            color: "red.500",
          },
        }),
        multiValue: (provided: SystemStyleObject) => ({
          ...provided,
          h: "6",
          bg: "grey.100",
          mr: 1,
        }),
        option: (provided: SystemStyleObject, { data }: { data: IOption }) => ({
          ...provided,
          fontSize: "sm",
          overflow: "none",
          h: "auto",
          minH: "10",
          ...((data.value === "select-all" ||
            data.value === "unselect-all") && {
            color: "#0B74A1",
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: 500,
          }),
        }),
        dropdownIndicator: (provided: SystemStyleObject) => ({
          ...provided,
          background: "transparent",
          color: "grey.500",
          p: 0,
          w: "30px",
        }),
        valueContainer: (provided: SystemStyleObject) => ({
          ...provided,
          pl: 2,
          pr: 0,
        }),
        ...(customChakraStyles as ChakraStylesConfig<
          IOption,
          boolean,
          GroupBase<IOption>
        >),
      }),
      [customChakraStyles, menuMinWidth],
    );

    const mergedStyles: StylesConfig<
      IOption,
      boolean,
      GroupBase<IOption>
    > = useMemo(
      () => ({
        menuPortal: (provided: Record<string, unknown>) => ({
          ...provided,
          zIndex: 9999,
        }),
        ...(customStyles as StylesConfig<IOption, boolean, GroupBase<IOption>>),
      }),
      [customStyles],
    );

    return (
      <Component<IOption, boolean, GroupBase<IOption>>
        {...props}
        // @ts-expect-error
        ref={selectRef}
        filterOption={ignoreFilterOptions ? () => true : selectFilterOption}
        // @ts-expect-error
        value={
          isCreatable
            ? selected || value
              ? { label: value, value }
              : []
            : selected
        }
        isLoading={isLoading}
        onChange={(data) => {
          setSelectedOnSelectChange(data);
          const selectedValues = parseValues(data);
          onChange?.(isMulti ? selectedValues : selectedValues[0]);
        }}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        onMenuOpen={handleMenuOpen}
        menuPlacement="auto"
        chakraStyles={mergedChakraStyles}
        isMulti={isMulti}
        // @ts-expect-error
        components={memoizedComponents}
        formatGroupLabel={(data) => data.label}
        isOptionDisabled={(option) => !!option.disabled}
        menuPortalTarget={asPortal ? document.body : undefined}
        styles={mergedStyles}
      />
    );
  },
);

const Select = forwardRef<SelectRef, SelectProps>(function Select(
  { error, label, ...props },
  ref,
) {
  return (
    <FormControl w="full" isInvalid={!!error}>
      {label && <FormLabel>{label}</FormLabel>}
      <SelectInternal ref={ref} {...props} />
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
});

export default Select;
